import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import ProductCard from "../components/Route/ProductCard/ProductCard";
import styles from "../styles/styles";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import Loader from "../components/Layout/Loader";
import { server } from "../server";
import { useSelector } from "react-redux";
import { getCoordinatesFromPin } from "../utils/pincode";
import { calculateDistance } from "../utils/distance";

const SearchResultsPage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialSearchTerm = searchParams.get("term");
  const imageSearch = searchParams.get("image");
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || "");
  const [searchData, setSearchData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // User coordinates from Redux store
  const { coordinates: userCoordinates } = useSelector((state) => state.user);

  // Distance calculations
  const [productDistances, setProductDistances] = useState({});
  const [distanceCalculationDone, setDistanceCalculationDone] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 30;
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);

  const [isDistanceDropdownOpen, setIsDistanceDropdownOpen] = useState(false);
  const [selectedDistanceCategories, setSelectedDistanceCategories] = useState({
    "Within 10Km / Quick Delivery": true,
    "Within 10-50km / Normal Delivery": true,
    "Within 50-100km / 2 Day Delivery": true,
    "Within 100-1000km / 3 Day Delivery": true,
    "Above 1000km / 1 Week Delivery": false,
  });

  const distanceCategoryLabels = [
    "Within 10Km / Quick Delivery",
    "Within 10-50km / Normal Delivery",
    "Within 50-100km / 2 Day Delivery",
    "Within 100-1000km / 3 Day Delivery",
    "Above 1000km / 1 Week Delivery",
  ];

  const categoryDropdownRef = useRef(null);
  const tagsDropdownRef = useRef(null);
  const distanceDropdownRef = useRef(null);

  // Fetch search results based on searchTerm
  const fetchSearchResults = useCallback(async () => {
    if (imageSearch) return; // No need to fetch if it's an image search

    setIsLoading(true);
    try {
      const query = new URLSearchParams({ term: searchTerm }).toString();
      const response = await fetch(`${server}/product/search?${query}`, {
        headers: { Accept: "application/json" },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const products = Array.isArray(result.products) ? result.products : [];
      setSearchData(products);
      setCategories(result.categories || []);
      setTags(result.tags || []);
      setFilteredData(products);
    } catch (error) {
      console.error("Error fetching search results:", error);
      setSearchData([]);
      setFilteredData([]);
      setCategories([]);
      setTags([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, imageSearch]);

  // Refetch results if the URL's search term changes (for text search)
  useEffect(() => {
    const newSearchTerm = searchParams.get("term") || "";
    setSearchTerm(newSearchTerm);
  }, [location.search]); // Use location.search as dependency

  // Fetch search results if searchTerm changes (for text search)
  useEffect(() => {
    if (!imageSearch && searchTerm) fetchSearchResults();
  }, [searchTerm, imageSearch, fetchSearchResults]);

  // Set filtered data based on the search results (for image search)
  useEffect(() => {
    if (imageSearch) {
      const data = location.state?.imageSearchData || [];
      setFilteredData(data); // Use state from location
      setSearchData(data); // Set searchData for pagination

      // Set categories and tags from location.state if available, else derive from data
      if (location.state?.categories && location.state.categories.length > 0) {
        setCategories(location.state.categories);
      } else {
        const derivedCategories = [...new Set(data.map((p) => p.category))];
        setCategories(derivedCategories);
      }

      if (location.state?.tags && location.state.tags.length > 0) {
        setTags(location.state.tags);
      } else {
        const derivedTags = [...new Set(data.flatMap((p) => p.tags))];
        setTags(derivedTags);
      }
    }
  }, [imageSearch, location.state]);

  // Compute distances after search data is loaded
  useEffect(() => {
    const computeDistances = async () => {
      if (!userCoordinates || !searchData || searchData.length === 0) {
        setDistanceCalculationDone(true);
        return;
      }

      const distancePromises = searchData.map(async (product) => {
        if (product.shop && product.shop.zipCode) {
          try {
            const sellerCoordinates = await getCoordinatesFromPin(product.shop.zipCode);
            const distance = calculateDistance(
              userCoordinates.lat,
              userCoordinates.lon,
              sellerCoordinates.lat,
              sellerCoordinates.lon
            );
            return { productId: product._id, distance };
          } catch (error) {
            console.error("Error fetching seller coordinates:", error);
            return { productId: product._id, distance: null };
          }
        } else {
          return { productId: product._id, distance: null };
        }
      });

      const distancesArray = await Promise.all(distancePromises);

      const distances = {};
      distancesArray.forEach(({ productId, distance }) => {
        distances[productId] = distance;
      });

      setProductDistances(distances);
      setDistanceCalculationDone(true);
    };

    computeDistances();
  }, [searchData, userCoordinates]);

  // Handle category filter change
  const handleCategoryChange = (category) => {
    setSelectedCategories((prevCategories) =>
      prevCategories.includes(category)
        ? prevCategories.filter((cat) => cat !== category)
        : [...prevCategories, category]
    );
  };

  // Handle tag filter change
  const handleTagChange = (tag) => {
    setSelectedTags((prevTags) =>
      prevTags.includes(tag)
        ? prevTags.filter((t) => t !== tag)
        : [...prevTags, tag]
    );
  };

  // Handle distance category change
  const handleDistanceCategoryChange = (category) => {
    setSelectedDistanceCategories((prevState) => ({
      ...prevState,
      [category]: !prevState[category],
    }));
  };

  const distanceCategoryCheck = (distance) => {
    if (distance <= 10) {
      return "Within 10Km / Quick Delivery";
    } else if (distance > 10 && distance <= 50) {
      return "Within 10-50km / Normal Delivery";
    } else if (distance > 50 && distance <= 100) {
      return "Within 50-100km / 2 Day Delivery";
    } else if (distance > 100 && distance <= 1000) {
      return "Within 100-1000km / 3 Day Delivery";
    } else if (distance > 1000) {
      return "Above 1000km / 1 Week Delivery";
    } else {
      return "Unknown";
    }
  };

  useEffect(() => {
    let filteredProducts = [...searchData];
  
    // Filter by selected categories
    if (selectedCategories.length > 0) {
      filteredProducts = filteredProducts.filter((product) =>
        selectedCategories.includes(product.category)
      );
    }
  
    // Filter by selected tags
    if (selectedTags.length > 0) {
      filteredProducts = filteredProducts.filter((product) =>
        selectedTags.some((tag) => product.tags.includes(tag))
      );
    }
  
    // Apply distance filter only if user coordinates are set and distance calculation is done
    if (userCoordinates && distanceCalculationDone && Object.values(selectedDistanceCategories).some(v => v)) {
      filteredProducts = filteredProducts.filter((product) => {
        const distance = productDistances[product._id];
        if (distance != null) {
          const category = distanceCategoryCheck(distance);
          return selectedDistanceCategories[category];
        }
        // If no distance data is available, include the product by default
        return true;
      });
    }
  
    // Sort by price if needed
    if (sortBy) {
      filteredProducts.sort((a, b) => {
        const priceA = a.quantitySlabsWithCommission[0]?.originalPriceWithCommission || Infinity;
        const priceB = b.quantitySlabsWithCommission[0]?.originalPriceWithCommission || Infinity;
        return sortBy === "priceAsc" ? priceA - priceB : priceB - priceA;
      });
    }
  
    // Paginate the filtered results
    const startIndex = (currentPage - 1) * productsPerPage;
    const paginatedProducts = filteredProducts.slice(startIndex, startIndex + productsPerPage);
  
    setFilteredData(paginatedProducts);
  }, [
    selectedCategories,
    selectedTags,
    sortBy,
    searchData,
    currentPage,
    selectedDistanceCategories,
    productDistances,
    distanceCalculationDone,
    userCoordinates,
  ]);
  

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setIsCategoryDropdownOpen(false);
      }
      if (
        tagsDropdownRef.current &&
        !tagsDropdownRef.current.contains(event.target)
      ) {
        setIsTagsDropdownOpen(false);
      }
      if (
        distanceDropdownRef.current &&
        !distanceDropdownRef.current.contains(event.target)
      ) {
        setIsDistanceDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <>
      <Header />
      {isLoading ? (
        <Loader />
      ) : (
        <div className={`${styles.section}`}>
          {/* Filter and Sort Controls */}
          <div className="grid grid-cols-2 gap-2 mb-6 mt-4 sm:mt-8">
            {/* Category Filter */}
            <div className="relative w-full" ref={categoryDropdownRef}>
              <button
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                className="w-full bg-white border p-1 text-xs sm:text-sm flex items-center justify-between"
              >
                Select Category
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {/* Category Dropdown */}
              {isCategoryDropdownOpen && (
                <div className="absolute bg-white border mt-2 w-full z-10 max-h-64 overflow-auto">
                  {categories.length > 0 ? (
                    categories.map((category, idx) => (
                      <div key={idx} className="p-2">
                        <input
                          type="checkbox"
                          id={category}
                          value={category}
                          onChange={() => handleCategoryChange(category)}
                          checked={selectedCategories.includes(category)}
                        />
                        <label htmlFor={category} className="ml-2">
                          {category}
                        </label>
                      </div>
                    ))
                  ) : (
                    <div>No Categories Found</div>
                  )}
                </div>
              )}
            </div>

            {/* Tags Filter */}
            <div className="relative w-full" ref={tagsDropdownRef}>
              <button
                onClick={() => setIsTagsDropdownOpen(!isTagsDropdownOpen)}
                className="w-full bg-white border p-1 text-xs sm:text-sm flex items-center justify-between"
              >
                Select Make
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {/* Tags Dropdown */}
              {isTagsDropdownOpen && (
                <div className="absolute bg-white border mt-2 w-full z-10 max-h-64 overflow-auto">
                  {tags.length > 0 ? (
                    tags.map((tag, idx) => (
                      <div key={idx} className="p-2">
                        <input
                          type="checkbox"
                          id={tag}
                          value={tag}
                          onChange={() => handleTagChange(tag)}
                          checked={selectedTags.includes(tag)}
                        />
                        <label htmlFor={tag} className="ml-2">
                          {tag}
                        </label>
                      </div>
                    ))
                  ) : (
                    <div>No Tags Found</div>
                  )}
                </div>
              )}
            </div>

            {/* Distance Filter */}
            <div className="relative w-full" ref={distanceDropdownRef}>
              <button
                onClick={() => setIsDistanceDropdownOpen(!isDistanceDropdownOpen)}
                className="w-full bg-white border p-1 text-xs sm:text-sm flex items-center justify-between"
              >
                Select Distance
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {/* Distance Dropdown */}
              {isDistanceDropdownOpen && (
                <div className="absolute bg-white border mt-2 w-full z-10 max-h-64 overflow-auto">
                  {distanceCategoryLabels.map((label, idx) => (
                    <div key={idx} className="p-2">
                      <input
                        type="checkbox"
                        id={label}
                        value={label}
                        onChange={() => handleDistanceCategoryChange(label)}
                        checked={selectedDistanceCategories[label]}
                      />
                      <label htmlFor={label} className="ml-2">
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sort by Price */}
            <div className="w-full">
              <select
                onChange={(e) => setSortBy(e.target.value)}
                value={sortBy}
                className="border p-1 text-xs sm:text-sm w-full"
              >
                <option value="">Sort by Price</option>
                <option value="priceAsc">Low to High</option>
                <option value="priceDesc">High to Low</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div className="w-full col-span-2">
              <button
                onClick={() => {
                  setSelectedCategories([]);
                  setSelectedTags([]);
                  setSelectedDistanceCategories({
                    "Within 10Km / Quick Delivery": true,
                    "Within 10-50km / Normal Delivery": true,
                    "Within 50-100km / 2 Day Delivery": true,
                    "Within 100-1000km / 3 Day Delivery": true,
                    "Above 1000km / 1 Week Delivery": false,
                  });
                  setSortBy("");
                }}
                className="w-full border p-1 text-xs sm:text-sm"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Search Results */}
          {filteredData.length > 0 ? (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 lg:grid-cols-5">
    {filteredData.map((product) => (
      <ProductCard
        data={product}
        key={product._id}
        distance={productDistances[product._id]}
      />
    ))}
  </div>
) : (
  <h1 className="text-center w-full pb-24 text-lg">
    {imageSearch
      ? "No products found. Please try entering a different pincode or adjusting the distance filter and re search."
      : "No products found. Please try adjusting your search term or filters including the distance filter."}
  </h1>
)}

          {/* Pagination */}
          <div className="flex justify-center mt-8">
            {Array.from(
              {
                length: Math.ceil(searchData.length / productsPerPage),
              },
              (_, i) => (
                <button
                  key={i}
                  onClick={() => handlePageChange(i + 1)}
                  className={`p-2 border ${
                    currentPage === i + 1 ? "bg-gray-200" : "bg-white"
                  }`}
                >
                  {i + 1}
                </button>
              )
            )}
          </div>
        </div>
      )}
      <Footer />
    </>
  );
};

export default SearchResultsPage;
